import { AuthenticationPage, useAuth } from '@ais-ui/auth';
import { SessionProvider } from '@ais-ui/websockets';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MapPage } from '../components/MapPage';
import { Sidebar } from '../components/sidebar/Sidebar';
import { FilterProvider } from '../modules/Map/FilterContext';
import { ROUTES } from '../routes';
import {
  SettingsProvider,
  useSettings,
} from '../modules/Settings/SettingsProvider';
import { PermissionsProvider } from '../modules/Permissions/PermissionsProvider';
import { SettingItem, SettingsPage } from '../modules/Settings/SettingsPage';
import { MapPanelProvider } from '../modules/Map/MapPanel/MapPanelContext';
import { NotificationProvider } from '../modules/Notifications/NotificationContext';
import { TooltipProvider } from '@ais-ui/ui-core';

import { ReportProvider } from '../modules/Reports/ReportContext';
import { ReleaseNotes } from '../modules/ReleaseNotes/ReleaseNotes';
import { ReplayProvider } from '../modules/Replay/ReplayContext';
import { TimeZoneProvider } from '@ais-ui/utils';
import { Help, HelpProvider } from '@ais-ui/help';
import { CoverageProvider } from '../modules/Map/Coverage/CoverageContext';
import { ReleaseNotesProvider } from '../modules/ReleaseNotes/ReleaseNotesProvider';
import { MultiFactorPage } from '../modules/MultiFactor/MultiFactorPage';
import { SettingsPageProvider } from '../modules/Settings/SettingsPageProvider';
import {
  useViewMode,
  ViewMode,
  ViewModeProvider,
} from '../components/ViewModeProvider';
import { useMemo } from 'react';
import { DetailsPage } from '../components/DetailsPage/DetailsPage';
import { SelectionStateProvider } from '../components/SelectionStateProvider';
import { AsyncJobsProvider } from '@ais-ui/async-jobs';
import { ReportExportManager } from '../modules/Reports/ReportExportManager';
import { GPSLostProvider } from '../modules/Map/GpsLost/GPSLostContext';

// We've created this component to avoid rerenders of the providers
//  when activeComponent is changed
const AppContent: React.FC = () => {
  const location = useLocation();
  const { viewMode } = useViewMode();

  const element = useMemo(() => {
    if (viewMode === ViewMode.Replay || viewMode === ViewMode.Normal) {
      return <MapPage></MapPage>;
    } else if (viewMode === ViewMode.Detail) {
      return <DetailsPage></DetailsPage>;
    } else {
      throw new Error(`Unknown view mode: ${viewMode}`);
    }
  }, [viewMode]);

  if (location.pathname.includes(ROUTES.SETTINGS.path)) {
    let initialActiveSettingItem: SettingItem;

    switch (location.pathname) {
      case ROUTES.SETTINGS.GENERAL.path:
        initialActiveSettingItem = SettingItem.General;
        break;
      case ROUTES.SETTINGS.MAP.path:
        initialActiveSettingItem = SettingItem.Map;
        break;
      case ROUTES.SETTINGS.WEB_MAP_SERVICES.path:
        initialActiveSettingItem = SettingItem.WMS;
        break;
      case ROUTES.SETTINGS.VECTOR_TILES.path:
        initialActiveSettingItem = SettingItem.VectorTiles;
        break;
      case ROUTES.SETTINGS.BASE_MAP.path:
        initialActiveSettingItem = SettingItem.BaseMap;
        break;
      case ROUTES.SETTINGS.BOOKMARKS.path:
        initialActiveSettingItem = SettingItem.Bookmarks;
        break;
      case ROUTES.SETTINGS.AUTHENTICATION.path:
        initialActiveSettingItem = SettingItem.Authentication;
        break;
      default:
        initialActiveSettingItem = SettingItem.General; // or any default value you prefer
        break;
    }

    return (
      <Routes>
        <Route
          path={location.pathname}
          element={
            <SettingsPageProvider>
              <SettingsPage
                initialActiveSettingItem={initialActiveSettingItem}
              ></SettingsPage>
            </SettingsPageProvider>
          }
        />
      </Routes>
    );
  }

  if (location.pathname.includes(ROUTES.MULTIFACTOR.AUTHENTICATED.path)) {
    return (
      <Routes>
        <Route
          path={ROUTES.MULTIFACTOR.AUTHENTICATED.path}
          element={<MultiFactorPage authenticated={true}></MultiFactorPage>}
        />
        ;
      </Routes>
    );
  }
  return (
    <div className="h-screen w-screen flex overflow-x-hidden">
      <div className="flex-shrink">
        <Sidebar id="main-sidebar" />
      </div>
      <div className="relative flex-grow">
        <Routes>
          <Route path={ROUTES.MAP.path} element={element} />
        </Routes>
      </div>
    </div>
  );
};

const TimeZoneRoute = () => {
  const { settings, dataUpdatedAt } = useSettings();

  return (
    <TimeZoneProvider settings={settings} dataUpdatedAt={dataUpdatedAt}>
      <TooltipProvider>
        <AsyncJobsProvider>
          <ReportExportManager />
          <NotificationProvider>
            <SelectionStateProvider>
              <ViewModeProvider>
                <FilterProvider>
                  <ReportProvider>
                    <CoverageProvider>
                      <GPSLostProvider>
                        <MapPanelProvider>
                          <HelpProvider>
                            <ReleaseNotesProvider>
                              <AppContent />
                              <Help />
                              <ReleaseNotes />
                            </ReleaseNotesProvider>
                          </HelpProvider>
                        </MapPanelProvider>
                      </GPSLostProvider>
                    </CoverageProvider>
                  </ReportProvider>
                </FilterProvider>
              </ViewModeProvider>
            </SelectionStateProvider>
          </NotificationProvider>
        </AsyncJobsProvider>
      </TooltipProvider>
    </TimeZoneProvider>
  );
};

const AuthenticatedRoute: React.FC = () => {
  const authContext = useAuth();
  const { sessions } = authContext;
  const location = useLocation(); // Get current location

  // If not logged in, redirect to login page.
  if (!sessions || !sessions.olSession) {
    return <Navigate to={ROUTES.LOGIN.path} />;
  }

  // If logged in and at the root '/', redirect to the map route.
  if (location.pathname === '/') {
    return <Navigate to={ROUTES.MAP.path} replace />;
  }

  return (
    <SessionProvider
      logout={authContext.logout}
      reconnect={authContext.reconnect}
    >
      <PermissionsProvider>
        <ReplayProvider>
          <SettingsProvider>
            <TimeZoneRoute></TimeZoneRoute>
          </SettingsProvider>
        </ReplayProvider>
      </PermissionsProvider>
    </SessionProvider>
  );
};

export function App() {
  return (
    <div data-theme="light">
      <Routes>
        <Route path={ROUTES.LOGIN.path} element={<AuthenticationPage />} />
        <Route
          path={ROUTES.MULTIFACTOR.BEFORE_LOGIN.path}
          element={<MultiFactorPage authenticated={false}></MultiFactorPage>}
        />
        <Route path="*" element={<AuthenticatedRoute />} />
      </Routes>
    </div>
  );
}

export default App;
